/**
 * TaskFilterBar (§1.6 Build 3) — single-select segmented control (All / Critical /
 * To do / Waiting / Done) + free-text title search. Session-local; combines AND with
 * the search box. Styled to the executor task-list theme (no existing app segmented
 * convention to reuse — ui-patterns.md has none).
 */
import { Search, X } from "lucide-react";
import { FILTERS, type FilterKey } from "@/lib/executor-task-filter";
import { SAGE, WARM } from "@shared/brand";


export function TaskFilterBar({
  filter, onFilter, query, onQuery,
}: {
  filter: FilterKey;
  onFilter: (f: FilterKey) => void;
  query: string;
  onQuery: (q: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-5">
      <div className="flex flex-wrap gap-1">
        {FILTERS.map((f) => {
          const active = f === filter;
          return (
            <button
              key={f}
              type="button"
              onClick={() => onFilter(f)}
              className="rounded-full px-3 py-1 text-[13px] font-medium transition-colors"
              style={{
                letterSpacing: "0.04em",
                backgroundColor: active ? SAGE[600] : WARM.white,
                color: active ? "#FFFFFF" : WARM.charcoal,
                border: `1px solid ${active ? SAGE[600] : WARM.stone}`,
              }}
              data-testid={`filter-${f.replace(/\s+/g, "-").toLowerCase()}`}
              aria-pressed={active}
            >
              {f}
            </button>
          );
        })}
      </div>

      <div className="relative flex items-center ml-auto">
        <Search className="h-4 w-4 absolute left-2.5 pointer-events-none" style={{ color: WARM.taupe }} />
        <input
          type="text"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search tasks…"
          className="rounded-full pl-8 pr-8 py-1.5 text-[14px] outline-none w-[220px] max-w-full"
          style={{ backgroundColor: WARM.white, border: `1px solid ${WARM.stone}`, color: WARM.charcoal }}
          data-testid="task-search"
        />
        {query && (
          <button
            type="button"
            onClick={() => onQuery("")}
            className="absolute right-2 p-0.5"
            style={{ color: WARM.taupe }}
            aria-label="Clear search"
            data-testid="task-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
